import { Box, Stack, Typography, alpha, useTheme } from '@mui/material';
import { SvgIcon } from '@mui/material';
import vmIcon from '@/assets/vm-icon.png';

function WrenchIcon() {
  return (
    <SvgIcon sx={{ fontSize: 16 }}>
      <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z" />
    </SvgIcon>
  );
}

const waveformBars = [18, 36, 24, 52, 34, 68, 44, 30, 58, 26, 46, 22, 40, 28];
const maintenanceNotes = ['Playlist glow-up', 'Smoother DJ flow', 'Back shortly'];

export function MaintenanceOverlay() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        backgroundColor: '#0B1120',
        backgroundImage: `
          linear-gradient(155deg, ${alpha(theme.palette.primary.dark, 0.38)} 0%, transparent 52%),
          radial-gradient(circle at 92% 6%, ${alpha(theme.palette.secondary.dark, 0.34)} 0%, transparent 30%),
          radial-gradient(circle at 8% 74%, ${alpha(theme.palette.warning.dark, 0.16)} 0%, transparent 28%),
          repeating-linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.025)} 0 1px, transparent 1px 18px)
        `,
        overflow: 'hidden',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          width: { xs: 240, sm: 360 },
          height: { xs: 240, sm: 360 },
          borderRadius: '50%',
          pointerEvents: 'none',
          filter: 'blur(1px)',
        },
        '&::before': { top: { xs: -120, sm: -170 }, right: { xs: -100, sm: -125 }, bgcolor: alpha(theme.palette.secondary.main, 0.13) },
        '&::after': { bottom: { xs: -150, sm: -190 }, left: { xs: -120, sm: -155 }, bgcolor: alpha(theme.palette.warning.main, 0.1) },
      }}
    >
      <Stack
        alignItems="center"
        spacing={{ xs: 2.4, sm: 2.8 }}
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 570,
          textAlign: 'center',
          px: { xs: 2.5, sm: 5 },
          py: { xs: 3.25, sm: 4.25 },
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: alpha(theme.palette.common.white, 0.065),
          border: `1px solid ${alpha(theme.palette.common.white, 0.13)}`,
          boxShadow: `0 28px 84px ${alpha(theme.palette.common.black, 0.36)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}`,
          backdropFilter: 'blur(24px)',
          '&::before': {
            content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3,
            background: `linear-gradient(90deg, ${theme.palette.warning.light}, ${theme.palette.secondary.light}, ${theme.palette.primary.light})`,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            component="img"
            src={vmIcon}
            alt=""
            sx={{
              width: { xs: 42, sm: 46 },
              height: { xs: 42, sm: 46 },
              borderRadius: 1.4,
              boxShadow: `0 0 0 1px ${alpha(theme.palette.common.white, 0.18)}, 0 14px 30px ${alpha(theme.palette.secondary.main, 0.24)}`,
            }}
          />
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ fontSize: { xs: 18, sm: 21 }, lineHeight: 1, fontWeight: 900, color: 'common.white' }}>
              Vibe Masters
            </Typography>
            <Typography sx={{ mt: 0.45, color: alpha(theme.palette.common.white, 0.68), fontSize: 12, lineHeight: 1, fontWeight: 700 }}>
              Crowd-powered sets
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.85} sx={{ px: 1.2, py: 0.65, borderRadius: 99, bgcolor: alpha(theme.palette.warning.main, 0.12), border: `1px solid ${alpha(theme.palette.warning.light, 0.27)}`, color: 'warning.light' }}>
          <WrenchIcon />
          <Typography sx={{ fontSize: 10.5, lineHeight: 1, fontWeight: 900, letterSpacing: '0.11em', textTransform: 'uppercase' }}>
            Soundcheck in progress
          </Typography>
        </Stack>

        <Stack spacing={1.2} sx={{ maxWidth: 440 }}>
          <Typography sx={{ fontSize: { xs: 28, sm: 34 }, fontWeight: 900, color: 'common.white', lineHeight: 1.1 }}>
            We’re tuning the booth
          </Typography>
          <Typography sx={{ fontSize: { xs: 15, sm: 16 }, color: alpha(theme.palette.common.white, 0.68), lineHeight: 1.6 }}>
            A few behind-the-decks upgrades are in progress. We’ll be back shortly.
          </Typography>
        </Stack>

        <Stack
          direction="row"
          alignItems="end"
          justifyContent="center"
          spacing={0.75}
          aria-hidden="true"
          sx={{ height: 58, width: '100%', maxWidth: 330, pt: 0.5 }}
        >
          {waveformBars.map((height, index) => (
            <Box
              key={`${height}-${index}`}
              sx={{
                width: { xs: 8, sm: 10 },
                height: Math.round(height * 0.72),
                borderRadius: 99,
                background: `linear-gradient(180deg, ${theme.palette.warning.light}, ${theme.palette.secondary.light})`,
                opacity: 0.35 + (index % 3) * 0.18,
                boxShadow: `0 8px 22px ${alpha(theme.palette.secondary.main, 0.22)}`,
                animation: `maintenanceWave ${1.1 + (index % 4) * 0.15}s ease-in-out ${index * 0.07}s infinite alternate`,
                '@keyframes maintenanceWave': { from: { transform: 'scaleY(0.58)' }, to: { transform: 'scaleY(1)' } },
              }}
            />
          ))}
        </Stack>

        <Stack
          spacing={1.25}
          sx={{
            width: '100%',
            maxWidth: 430,
            pt: 1.5,
            borderTop: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
          }}
        >
          <Typography sx={{ color: alpha(theme.palette.common.white, 0.46), fontSize: 11, fontWeight: 900, letterSpacing: 1.2, textTransform: 'uppercase' }}>
            Current updates
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.65, sm: 0.85 }} sx={{ justifyContent: 'center' }}>
            {maintenanceNotes.map((note) => (
              <Stack
                key={note}
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={0.8}
                sx={{ px: 1, py: 0.65, borderRadius: 99, color: alpha(theme.palette.common.white, 0.72), fontSize: 11.5, fontWeight: 800, lineHeight: 1.2, bgcolor: alpha(theme.palette.common.white, 0.045), border: `1px solid ${alpha(theme.palette.common.white, 0.08)}` }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'warning.light',
                    boxShadow: `0 0 18px ${alpha(theme.palette.warning.light, 0.7)}`,
                    flex: '0 0 auto',
                  }}
                />
                <Box component="span">{note}</Box>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
